(() => {
  'use strict';

  const $$ = (selector, root = document) => [...(root?.querySelectorAll(selector) || [])];
  const selectorEscape = (value) => window.CSS?.escape ? window.CSS.escape(String(value)) : String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  const $ = (selector, root = document) => root?.querySelector(selector) || null;
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const parseJson = (node, fallback = []) => {
    try { return node ? JSON.parse(node.textContent) : fallback; } catch { return fallback; }
  };

  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.loaded === 'true') return;
        const controller = new AbortController();
        this.abortController = controller;

        const load = async () => {
          if (this.dataset.loaded === 'true' || this.dataset.loading === 'true') return;
          this.dataset.loading = 'true';
          this.setAttribute('aria-busy', 'true');
          try {
            const response = await fetch(this.dataset.url, {
              signal: controller.signal,
              headers: { 'X-Requested-With': 'XMLHttpRequest' }
            });
            if (!response.ok) throw new Error(String(response.status));
            const text = await response.text();
            if (controller.signal.aborted || this.abortController !== controller) return;
            const fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('product-recommendations');
            this.dataset.loaded = 'true';
            if (!fresh?.innerHTML.trim()) {
              this.hidden = true;
              return;
            }
            this.replaceChildren(...fresh.cloneNode(true).childNodes);
            this.hidden = false;
            window.URB?.refresh?.(this);
          } catch (error) {
            if (error.name !== 'AbortError') this.hidden = true;
          } finally {
            if (this.abortController === controller) {
              delete this.dataset.loading;
              this.removeAttribute('aria-busy');
            }
          }
        };

        // The empty Liquid placeholder is hidden; observe its visible section wrapper.
        const observationTarget = this.hidden ? this.parentElement : this;
        if ('IntersectionObserver' in window && observationTarget) {
          this.observer = new IntersectionObserver((entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            load();
          }, { rootMargin: '400px 0px' });
          this.observer.observe(observationTarget);
        } else {
          load();
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.abortController?.abort();
        delete this.dataset.loading;
      }
    });
  }

  const initProductPage = (root) => {
    if (!root || root.dataset.productPageInitialized === 'true') return;
    root.dataset.productPageInitialized = 'true';
    const controller = new AbortController();
    root.urbAbortController = controller;
    const { signal } = controller;
    const stage = $('[data-gallery-stage]', root);
    const media = $$('[data-gallery-media]', root);
    const thumbs = $$('[data-gallery-thumb]', root);
    const counter = $('[data-gallery-counter]', root);
    let activeIndex = Math.max(0, media.findIndex((item) => item.classList.contains('is-active') || item.classList.contains('active')));
    const shouldHideInactiveMedia = () => root.dataset.galleryLayout === 'focus'
      && !(window.matchMedia('(max-width: 989px)').matches && root.dataset.mobileGallery === 'stack');

    const syncCounter = () => {
      if (!counter) return;
      const number = $('b', counter);
      if (number) number.textContent = String(activeIndex + 1);
      else counter.textContent = `${activeIndex + 1} / ${media.length}`;
    };

    const syncActiveMedia = () => {
      media.forEach((item, index) => {
        const video = $('video', item);
        if (index === activeIndex && root.dataset.videoAutoplay === 'true') {
          const playResult = video?.play?.();
          playResult?.catch?.(() => {});
        } else {
          video?.pause?.();
        }
        const model = $('model-viewer', item);
        if (index !== activeIndex) model?.pause?.();
      });
    };

    const activate = (index, { scroll = true, focusThumb = false, behavior = null } = {}) => {
      if (!media.length) return;
      activeIndex = (index + media.length) % media.length;
      media.forEach((item, itemIndex) => {
        const active = itemIndex === activeIndex;
        item.classList.toggle('active', active);
        item.classList.toggle('is-active', active);
        const inactive = shouldHideInactiveMedia() && !active;
        item.setAttribute('aria-hidden', String(inactive));
        item.toggleAttribute('inert', inactive);
      });
      thumbs.forEach((thumb, itemIndex) => {
        const active = itemIndex === activeIndex;
        thumb.setAttribute('aria-current', String(active));
        thumb.setAttribute('aria-selected', String(active));
        thumb.tabIndex = active ? 0 : -1;
        if (active && focusThumb) thumb.focus({ preventScroll: true });
      });
      syncCounter();
      syncActiveMedia();
      if (scroll && stage && media[activeIndex]) {
        const horizontal = stage.scrollWidth > stage.clientWidth + 4;
        if (horizontal) {
          const target = media[activeIndex];
          stage.scrollTo({
            left: target.offsetLeft - Math.max(0, (stage.clientWidth - target.offsetWidth) / 2),
            behavior: behavior || (reduceMotion() ? 'auto' : 'smooth')
          });
        } else if (root.dataset.galleryLayout !== 'focus') {
          media[activeIndex].scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'nearest' });
        }
      }
    };

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => activate(index), { signal });
      thumb.addEventListener('keydown', (event) => {
        if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home') activate(0, { focusThumb: true });
        else if (event.key === 'End') activate(media.length - 1, { focusThumb: true });
        else activate(index + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1), { focusThumb: true });
      }, { signal });
    });

    $('[data-gallery-prev]', root)?.addEventListener('click', () => activate(activeIndex - 1), { signal });
    $('[data-gallery-next]', root)?.addEventListener('click', () => activate(activeIndex + 1), { signal });
    stage?.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      activate(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    }, { signal });

    window.addEventListener('resize', () => activate(activeIndex, { scroll: false }), { passive: true, signal });

    if (stage && media.length > 1) {
      let scrollTimer;
      stage.addEventListener('scroll', () => {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(() => {
          if (stage.scrollWidth <= stage.clientWidth + 4) return;
          const center = stage.scrollLeft + stage.clientWidth / 2;
          const closest = media.reduce((best, item, index) => {
            const distance = Math.abs((item.offsetLeft + item.offsetWidth / 2) - center);
            return distance < best.distance ? { index, distance } : best;
          }, { index: activeIndex, distance: Number.POSITIVE_INFINITY });
          if (closest.index !== activeIndex) activate(closest.index, { scroll: false });
        }, 80);
      }, { passive: true, signal });
    }

    activate(activeIndex, {
      scroll: window.matchMedia('(max-width: 989px)').matches && root.dataset.mobileGallery === 'carousel',
      behavior: 'auto'
    });

    root.addEventListener('urb:variant-change', (event) => {
      const id = event.detail?.variant?.featured_media_id;
      if (!id) return;
      const index = media.findIndex((item) => String(item.dataset.galleryMedia) === String(id));
      if (index >= 0) activate(index);
    }, { signal });

    // Store the clicked image index before the global dialog handler opens it.
    root.addEventListener('click', (event) => {
      const zoom = event.target.closest('[data-media-zoom]');
      if (!zoom) return;
      const dialog = $('[data-media-lightbox]');
      if (!dialog) return;
      const images = parseJson($('[data-lightbox-images]', dialog));
      const mediaId = zoom.closest('[data-gallery-media]')?.dataset.galleryMedia;
      const index = Math.max(0, images.findIndex((item) => String(item.media_id) === String(mediaId)));
      dialog.dataset.lightboxIndex = String(index);
    }, { signal });

    // Dedicated sticky observer also works after a section is reloaded in the theme editor.
    const addDeliveryDays = (start, amount, weekdaysOnly) => {
      const date = new Date(start);
      let remaining = Math.max(0, Number(amount || 0));
      while (remaining > 0) {
        date.setDate(date.getDate() + 1);
        const weekday = date.getDay();
        if (!weekdaysOnly || (weekday !== 0 && weekday !== 6)) remaining -= 1;
      }
      return date;
    };
    $$('[data-delivery-estimate]', root).forEach((estimate) => {
      const output = $('[data-delivery-text]', estimate);
      if (!output) return;
      const minDays = Math.max(0, Number(estimate.dataset.minDays || 0));
      const maxDays = Math.max(minDays, Number(estimate.dataset.maxDays || minDays));
      const weekdaysOnly = estimate.dataset.weekdaysOnly === 'true';
      const cutoff = Math.min(23, Math.max(0, Number(estimate.dataset.cutoffHour || 23)));
      let base = new Date();
      if (base.getHours() >= cutoff) base = addDeliveryDays(base, 1, weekdaysOnly);
      const first = addDeliveryDays(base, minDays, weekdaysOnly);
      const last = addDeliveryDays(base, maxDays, weekdaysOnly);
      const locale = document.documentElement.lang || 'pt-BR';
      const sameYear = first.getFullYear() === last.getFullYear();
      const formatter = new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
      const template = estimate.dataset.template || '[inicio] – [fim]';
      const range = template
        .split('[inicio]').join(formatter.format(first))
        .split('[fim]').join(formatter.format(last));
      estimate.dataset.deliveryRange = range;

      const postalForm = $('[data-postal-form]', estimate);
      if (!postalForm) {
        output.textContent = range;
        return;
      }

      const postalInput = $('input[name="postal_code"]', postalForm);
      const postalButton = $('[data-postal-submit]', postalForm);
      const fallbackText = estimate.dataset.fallbackText || output.textContent;
      const loadingText = estimate.dataset.postalLoading || 'Consultando o CEP…';
      const invalidText = estimate.dataset.postalInvalid || 'Informe um CEP válido.';
      const successTemplate = estimate.dataset.postalSuccess || 'Entrega para [cidade] - [uf]. Previsão: [prazo]';
      output.textContent = fallbackText;

      postalInput?.addEventListener('input', () => {
        const digits = postalInput.value.replace(/\D/g, '').slice(0, 8);
        postalInput.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
        postalInput.removeAttribute('aria-invalid');
      }, { signal });

      const lookupPostalCode = async () => {
        const postalCode = postalInput?.value.replace(/\D/g, '') || '';
        if (postalCode.length !== 8) {
          postalInput?.setAttribute('aria-invalid', 'true');
          output.textContent = invalidText;
          postalInput?.focus({ preventScroll: true });
          return;
        }

        postalForm.setAttribute('aria-busy', 'true');
        if (postalButton) postalButton.disabled = true;
        output.textContent = loadingText;
        try {
          const response = await fetch(`https://viacep.com.br/ws/${postalCode}/json/`, {
            signal,
            headers: { Accept: 'application/json' }
          });
          if (!response.ok) throw new Error(String(response.status));
          const address = await response.json();
          if (address.erro || !address.localidade || !address.uf) throw new Error('postal-code');
          output.textContent = successTemplate
            .split('[cidade]').join(address.localidade)
            .split('[uf]').join(address.uf)
            .split('[prazo]').join(range);
          postalInput?.removeAttribute('aria-invalid');
        } catch (error) {
          if (error.name === 'AbortError') return;
          postalInput?.setAttribute('aria-invalid', 'true');
          output.textContent = invalidText;
        } finally {
          postalForm.removeAttribute('aria-busy');
          if (postalButton) postalButton.disabled = false;
        }
      };

      postalButton?.addEventListener('click', lookupPostalCode, { signal });
      postalInput?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        lookupPostalCode();
      }, { signal });
    });

    $$('[data-gift-recipient]', root).forEach((recipient) => {
      if (recipient.dataset.initialized === 'true') return;
      recipient.dataset.initialized = 'true';
      const toggle = $('[data-recipient-toggle]', recipient);
      const fields = $('[data-recipient-fields]', recipient);
      const control = $('[data-recipient-control]', recipient);
      const offset = $('[data-recipient-offset]', recipient);
      const inputs = $$('[data-recipient-input]', recipient);
      const email = $('[data-recipient-required]', recipient);
      const date = $('[data-recipient-date]', recipient);
      const message = $('[data-recipient-message]', recipient);
      const count = $('[data-recipient-count]', recipient);
      const status = $('[data-recipient-status]', recipient);

      const formatDate = (value) => {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const maxDate = new Date(today);
      maxDate.setDate(maxDate.getDate() + 90);
      if (date) {
        date.min = formatDate(today);
        date.max = formatDate(maxDate);
      }

      const syncCount = () => {
        if (count && message) count.textContent = String(message.value.length);
      };
      const sync = (announce = false) => {
        const active = Boolean(toggle?.checked);
        if (fields) fields.hidden = !active;
        inputs.forEach((input) => { input.disabled = !active; });
        if (email) email.required = active;
        if (offset) {
          offset.disabled = !active;
          offset.value = active ? String(new Date().getTimezoneOffset()) : '';
        }
        if (control) control.value = active ? 'true' : 'if_present';
        if (announce && status) {
          status.textContent = active ? recipient.dataset.enabledText : recipient.dataset.disabledText;
        }
        if (active) window.requestAnimationFrame(() => email?.focus({ preventScroll: true }));
      };
      toggle?.addEventListener('change', () => sync(true), { signal });
      message?.addEventListener('input', syncCount, { signal });
      syncCount();
      sync(false);
    });

    const sectionId = root.dataset.sectionId;
    const sticky = sectionId ? document.querySelector(`[data-sticky-buy][data-section-id="${selectorEscape(sectionId)}"]`) : null;
    const primaryButton = $('[data-add]', root);
    if (sticky && primaryButton && !sticky.dataset.observerInitialized && 'IntersectionObserver' in window) {
      sticky.dataset.observerInitialized = 'true';
      const observer = new IntersectionObserver(([entry]) => {
        sticky.classList.toggle('visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0 });
      observer.observe(primaryButton);
      sticky.urbObserver = observer;
    }

    activate(activeIndex, { scroll: false });
  };

  const initLightbox = (dialog) => {
    if (!dialog || dialog.dataset.pdpInitialized === 'true') return;
    dialog.dataset.pdpInitialized = 'true';
    const images = parseJson($('[data-lightbox-images]', dialog));
    const image = $('[data-lightbox-image]', dialog);
    const counter = $('[data-lightbox-counter]', dialog);
    const viewport = $('.pdp-lightbox__viewport', dialog);
    const dots = $$('[data-lightbox-dot]', dialog);
    let index = Number(dialog.dataset.lightboxIndex || 0);
    let swipeStart = null;
    let suppressClickUntil = 0;
    let preloadTask = null;
    let focusReleaseTask = null;
    const preloaded = new Set();
    const releaseRestoredFocus = () => {
      window.clearTimeout(focusReleaseTask);
      focusReleaseTask = window.setTimeout(() => {
        const active = document.activeElement;
        if (!dialog.open && active?.closest?.('[data-media-zoom], [data-gallery-stage], [data-gallery-media]')) active.blur();
      }, 96);
    };

    const sourceFor = (item) => {
      const viewportWidth = viewport?.clientWidth || window.innerWidth || 1200;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      const targetWidth = Math.ceil(viewportWidth * pixelRatio);
      if (targetWidth <= 900 && item.src_900) return item.src_900;
      if (targetWidth <= 1400 && item.src_1400) return item.src_1400;
      if (targetWidth <= 2000 && item.src_2000) return item.src_2000;
      return item.src_3000 || item.src || item.src_2000 || item.src_1400;
    };

    const preloadNext = () => {
      if (images.length < 2 || navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
      const nextItem = images[(index + 1) % images.length];
      const src = sourceFor(nextItem);
      if (!src || preloaded.has(src)) return;
      preloaded.add(src);
      const preloadImage = new Image();
      preloadImage.decoding = 'async';
      preloadImage.src = src;
    };

    const schedulePreload = () => {
      if (preloadTask !== null) {
        if ('cancelIdleCallback' in window) window.cancelIdleCallback(preloadTask);
        else window.clearTimeout(preloadTask);
      }
      preloadTask = 'requestIdleCallback' in window
        ? window.requestIdleCallback(preloadNext, { timeout: 1400 })
        : window.setTimeout(preloadNext, 650);
    };

    const show = (nextIndex) => {
      if (!images.length || !image) return;
      index = (nextIndex + images.length) % images.length;
      const item = images[index];
      const src = sourceFor(item);
      dialog.dataset.lightboxIndex = String(index);
      image.alt = item.alt || '';
      if (item.width && item.height) {
        image.width = item.width;
        image.height = item.height;
      }
      if (src && image.dataset.activeSrc !== src) {
        dialog.classList.add('is-loading');
        image.fetchPriority = 'high';
        image.dataset.activeSrc = src;
        image.src = src;
        if (image.complete) requestAnimationFrame(() => {
          dialog.classList.remove('is-loading');
          schedulePreload();
        });
      } else {
        dialog.classList.remove('is-loading');
        schedulePreload();
      }
      if (counter) counter.textContent = `${index + 1} / ${images.length}`;
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.setAttribute('aria-current', String(active));
        dot.tabIndex = active ? 0 : -1;
      });
      const activeDot = dots[index];
      if (activeDot && dialog.open) activeDot.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      if (viewport) viewport.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    };

    image?.addEventListener('load', () => {
      dialog.classList.remove('is-loading');
      schedulePreload();
    });
    image?.addEventListener('error', () => dialog.classList.remove('is-loading'));

    dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-lightbox-prev]')) show(index - 1);
      if (event.target.closest('[data-lightbox-next]')) show(index + 1);
      const dot = event.target.closest('[data-lightbox-dot]');
      if (dot) show(Number(dot.dataset.lightboxDot));
      const zoom = event.target.closest('[data-media-zoom]');
      if (zoom) show(Number(dialog.dataset.lightboxIndex || 0));
      if (event.target === viewport) dialog.close();
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); show(index - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); show(index + 1); }
      if (event.target.closest('[data-lightbox-dot]') && event.key === 'Home') { event.preventDefault(); show(0); dots[0]?.focus(); }
      if (event.target.closest('[data-lightbox-dot]') && event.key === 'End') { event.preventDefault(); show(images.length - 1); dots.at(-1)?.focus(); }
      if (event.key === 'Escape' && dialog.open) { event.preventDefault(); dialog.close(); }
    });
    viewport?.addEventListener('pointerdown', (event) => {
      if (!['touch', 'pen'].includes(event.pointerType)) return;
      swipeStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
      try { viewport.setPointerCapture(event.pointerId); } catch (_) {}
    });
    viewport?.addEventListener('pointerup', (event) => {
      if (!swipeStart || event.pointerId !== swipeStart.id) return;
      const distanceX = event.clientX - swipeStart.x;
      const distanceY = event.clientY - swipeStart.y;
      swipeStart = null;
      try { viewport.releasePointerCapture(event.pointerId); } catch (_) {}
      if (Math.abs(distanceX) < 48 || Math.abs(distanceX) <= Math.abs(distanceY) * 1.15) return;
      show(index + (distanceX < 0 ? 1 : -1));
      suppressClickUntil = window.performance.now() + 400;
    });
    viewport?.addEventListener('pointercancel', () => { swipeStart = null; });
    viewport?.addEventListener('click', (event) => {
      if (window.performance.now() >= suppressClickUntil) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      suppressClickUntil = 0;
    }, true);
    dialog.addEventListener('toggle', () => {
      if (dialog.open) show(Number(dialog.dataset.lightboxIndex || 0));
      else {
        image.fetchPriority = 'low';
        releaseRestoredFocus();
      }
    });
    dialog.addEventListener('close', () => {
      image.fetchPriority = 'low';
      releaseRestoredFocus();
    });
    // showModal doesn't emit toggle consistently in older WebKit.
    const observer = new MutationObserver(() => {
      if (dialog.hasAttribute('open')) show(Number(dialog.dataset.lightboxIndex || 0));
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    dialog.urbLightboxObserver = observer;
  };

  const boot = (scope = document) => {
    $$('[data-product-page]', scope).forEach(initProductPage);
    $$('[data-media-lightbox]', scope === document ? document : scope.parentElement || document).forEach(initLightbox);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = $('[data-product-page]', event.target);
    root?.urbAbortController?.abort();
    const sectionId = root?.dataset.sectionId;
    const sticky = sectionId ? document.querySelector(`[data-sticky-buy][data-section-id="${selectorEscape(sectionId)}"]`) : null;
    sticky?.urbObserver?.disconnect();
    const lightbox = event.target.matches?.('[data-media-lightbox]') ? event.target : event.target.querySelector?.('[data-media-lightbox]');
    lightbox?.urbLightboxObserver?.disconnect();
  });
})();
